import { z } from 'zod';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/** Dry dock şemalarının ortak parçaları (ADR 0129): hata kodları tatbikat şemalarıyla aynı. */

const missing = (input: unknown) => input === undefined || input === null;
export const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
/** Uzun metin: satır sonları korunur, satır içi boşluk sadeleşir. */
const paragraph = (value: string) =>
  value
    .normalize('NFC')
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim().replace(/[ \t]+/g, ' '))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
export const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
export const requiredParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
export const optionalParagraph = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(paragraph)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export const day = z
  .string(typed('INVALID_DATE'))
  .regex(DAY, 'INVALID_DATE')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'INVALID_DATE');
export const optionalDay = z
  .union([z.literal(''), day], typed('INVALID_DATE'))
  .nullish()
  .transform((value) => (value ? value : null));
export const optionalUuid = z
  .uuid('INVALID_ID')
  .nullish()
  .transform((value) => value ?? null);

export function parsed<T>(result: z.ZodSafeParseResult<T>): ParseResult<T> {
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: toIssues(result.error).filter(
          (issue, index, all) => all.findIndex((i) => i.path === issue.path) === index,
        ),
      };
}
